import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const DISMISSED_KEY = 'eddm.install-dismissed';

/**
 * Offer to install the app.
 *
 * Chrome and Edge fire `beforeinstallprompt` when the app qualifies, and the
 * event must be captured and re-fired from a user gesture — calling prompt()
 * outside a click is ignored. iOS Safari fires nothing at all and installs only
 * through Share -> Add to Home Screen, so it gets instructions instead.
 *
 * Dismissal is remembered. A banner that returns on every load is worse than no
 * banner, and this one sits above a screen people use all day.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY) === '1') return;

    // Already installed: standalone display mode, or iOS's own flag.
    const installed =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (installed) return;

    const onPrompt = (event) => {
      // Chrome shows its own mini-infobar unless the event is cancelled.
      event.preventDefault();
      setDeferred(event);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS never fires the event, so detect it and offer the manual route.
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isSafari = /safari/i.test(window.navigator.userAgent) && !/crios|fxios/i.test(window.navigator.userAgent);
    if (isIos && isSafari) {
      setIosHint(true);
      setVisible(true);
    }

    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // The event is single-use either way; a declined prompt cannot be re-fired.
    setDeferred(null);
    setVisible(false);
    if (outcome === 'dismissed') localStorage.setItem(DISMISSED_KEY, '1');
  };

  if (!visible) return null;

  return (
    <div className="install-bar" role="region" aria-label="Install this app">
      <Download size={18} />
      <span className="install-text">
        {iosHint
          ? 'Install: tap Share, then “Add to Home Screen”.'
          : 'Install EDDM Organizer for a full-screen window and faster start-up.'}
      </span>

      {!iosHint && (
        <button className="btn btn-primary install-action" onClick={install}>
          Install
        </button>
      )}

      <button className="icon-button" onClick={dismiss} aria-label="Dismiss">
        <X size={18} />
      </button>
    </div>
  );
}
